import { Ban, Check, Clock,SquareCheck, X, XCircle } from "lucide-react";
import * as React from "react";
import { graphql, useFragment } from "react-relay";
import { Button } from "src/components/ui/button";
import { LoadingSpinner } from "src/components/ui/loading-spinner";

import { ToolCallButton_toolCall$key } from "./__generated__/ToolCallButton_toolCall.graphql";
import { getToolName, getToolQuestion } from "./utils";

interface ToolCallButtonProps {
  toolCall: ToolCallButton_toolCall$key;
  onClick: () => void;
  isPending?: boolean;
  isWaiting?: boolean;
  onApprove?: () => Promise<void>;
  onReject?: () => Promise<void>;
  approvalInFlight?: boolean;
}

const fragment = graphql`
  fragment ToolCallButton_toolCall on GraphQLMessageToolCall {
    toolUse {
      name
      displayName
      capabilityId
      input {
        __typename
        ... on DatadogLogsToolInput { question }
        ... on DatadogLogsAggregationToolInput { question }
        ... on DatadogMetricsToolInput { question }
        ... on DatadogSpansToolInput { question }
        ... on DatadogSpansAggregationToolInput { question }
        ... on DatadogRumAggregationToolInput { question }
        ... on DatadogEventsToolInput { question }
        ... on DatadogCIPipelineEventsToolInput { question }
        ... on DatadogCIPipelineEventsAggregationToolInput { question }
        ... on DatadogMonitorToolInput { question }
        ... on DatadogServiceDependencyToolInput { question }
        ... on DatadogErrorTrackingIssueDetailsToolInput { question }
        ... on DatadogErrorTrackingIssuesToolInput { question }
        ... on BuildkiteBuildsToolInput { question }
        ... on BuildkiteLogsToolInput { question }
        ... on BuildkiteAnnotationsToolInput { question }
        ... on BuildkiteArtifactsToolInput { question }
        ... on BuildkiteFlakyTestToolInput { question }
        ... on CloudWatchLogsToolInput { question }
        ... on CloudWatchLogsAggregationToolInput { question }
        ... on CloudWatchMetricsToolInput { question }
        ... on GrafanaCloudLogsToolInput { question }
        ... on GrafanaCloudLogsAggregationToolInput { question }
        ... on GrafanaCloudMetricsToolInput { question }
        ... on GrafanaCloudSpansAggregationToolInput { question }
        ... on GrafanaCloudSpansToolInput { question }
        ... on NewRelicLogsToolInput { question }
        ... on NewRelicLogsAggregationToolInput { question }
        ... on NewRelicMetricsToolInput { question }
        ... on NewRelicSpansToolInput { question }
        ... on NewRelicSpansAggregationToolInput { question }
        ... on NewRelicTransactionsToolInput { question }
        ... on NewRelicTransactionsAggregationToolInput { question }
        ... on QueryCodeToolInput { question }
        ... on GitHubActionsRunsToolInput { question }
        ... on GitHubActionsLogsToolInput { question }
        ... on SlackChannelContentsToolInput { question }
        ... on SlackChannelMetadataToolInput { question }
        ... on SlackUsergroupMentionToolInput { usergroupHandle }
        ... on SentryIssuesToolInput { question projectId environment sentryQueryParams: queryParams startIsoStr endIsoStr }
        ... on SentryIssueEventsToolInput { question issueId }
        ... on TemporalWorkflowsToolInput { question }
        ... on TemporalWorkflowHistoryToolInput { question }
        ... on UpdateTodoToolInput { tasks }
      }
    }
    toolResult {
      hasData
      commandFailed
    }
  }
`;

export const ToolCallButton: React.FC<ToolCallButtonProps> = ({
  toolCall,
  onClick,
  isPending = false,
  isWaiting = false,
  onApprove,
  onReject,
  approvalInFlight = false,
}) => {
  const data = useFragment(fragment, toolCall);
  const { toolResult } = data;

  // Check the status of the tool result
  const isLoading = !toolResult || toolResult?.hasData === null;
  const hasData = toolResult?.hasData;
  const commandFailed = toolResult?.commandFailed;

  // Extract tool name and question using utility functions
  const toolName = getToolName(data);
  const question = getToolQuestion(data);

  // If waiting (no approval needed, will run after approvals), show greyed out
  if (isWaiting) {
    return (
      <button
        onClick={onClick}
        className="flex items-center gap-2 bg-popover text-gray-500 text-xs rounded px-3 py-1.5 border border-border text-left w-fit cursor-default opacity-60"
      >
        <Clock className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="font-semibold">{toolName}</span>
        {question && (
          <>
            <span className="mx-1 text-gray-600">|</span>
            <span className="text-gray-600">{question}</span>
          </>
        )}
      </button>
    );
  }

  // If pending approval, show a different layout with inline buttons
  if (isPending) {
    return (
      <div className="flex items-center gap-2 bg-amber-950/20 hover:bg-amber-950/30 text-gray-300 text-xs rounded px-3 py-2 border border-amber-700/40 w-fit">
        <button onClick={onClick} className="flex items-center gap-2 flex-1">
          <Clock className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
          <span className="font-semibold">{toolName}</span>
          <span className="mx-1 text-gray-500">|</span>
          <span className="text-amber-300">Requires approval</span>
        </button>
        <div className="flex gap-1.5 ml-2 pl-2 border-l border-amber-700/40">
          <Button
            size="sm"
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              onApprove?.();
            }}
            disabled={approvalInFlight}
            className="h-7 px-2 flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {approvalInFlight ? (
              <LoadingSpinner className="h-3.5 w-3.5" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onReject?.();
            }}
            disabled={approvalInFlight}
            className="h-7 px-2 flex items-center gap-1.5 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {approvalInFlight ? (
              <LoadingSpinner className="h-3.5 w-3.5" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
            Reject
          </Button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 bg-muted hover:bg-accent text-foreground text-xs rounded px-3 py-1.5 border border-border text-left w-fit"
    >
      {isLoading ? (
        <LoadingSpinner className="h-3.5 w-3.5 flex-shrink-0" />
      ) : commandFailed ? (
        <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" aria-label="Command failed" />
      ) : hasData ? (
        <SquareCheck className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
      ) : (
        <Ban className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
      )}
      <span className="font-semibold">{toolName}</span>
      {question && (
        <>
          <span className="mx-1 text-muted-foreground">|</span>
          <span className="text-muted-foreground">{question}</span>
        </>
      )}
    </button>
  );
};